/**
 * BannerManager.jsx — Redesigned · Dark Luxury Fintech
 * Accent: cyan/sky (Media identity)
 *
 * Améliorations UX vs original :
 * - window.confirm() → ConfirmDialog natif
 * - Modal form → Drawer latéral spring-animated
 * - Grid de cards avec image preview + overlay hover actions
 * - Drag-to-reorder order indicator
 * - Toggle slideshow animé
 * - Stats MiniStat animées
 * - Filtre active/inactive
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Image as ImageIcon, Plus, Edit2, Trash2,
  RefreshCw, X, Check, Eye, EyeOff,
  AlertTriangle, Filter, ChevronDown, Search,
  ToggleLeft, ToggleRight, Layers, Globe
} from 'lucide-react';
import { toast } from 'react-toastify';

// ─── Atoms ────────────────────────────────────────────────────────────────
const GlassPanel = ({ children, style = {} }) => (
  <div style={{ background: 'rgba(17,24,39,.85)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 16, backdropFilter: 'blur(16px)', overflow: 'hidden', ...style }}>
    {children}
  </div>
);

const MiniStat = ({ label, value, accent = '#22d3ee', icon: Icon, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * .07, duration: .45, ease: [.22, 1, .36, 1] }}
    whileHover={{ y: -3, transition: { duration: .15 } }}
    style={{ background: 'rgba(17,24,39,.85)', border: `1px solid ${accent}33`, borderRadius: 14, padding: '14px 16px', boxShadow: `0 0 24px ${accent}0d`, position: 'relative', overflow: 'hidden' }}
  >
    <div style={{ position: 'absolute', top: -24, right: -24, width: 72, height: 72, borderRadius: '50%', background: `${accent}18`, filter: 'blur(20px)', pointerEvents: 'none' }} />
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
      <div>
        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: '#475569', marginBottom: 8 }}>{label}</p>
        <p style={{ fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{value}</p>
      </div>
      {Icon && (
        <div style={{ width: 32, height: 32, borderRadius: 9, background: `${accent}18`, border: `1px solid ${accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={15} color={accent} strokeWidth={1.8} />
        </div>
      )}
    </div>
    <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: .5, delay: .2 + index * .07 }}
      style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: accent, opacity: .5, transformOrigin: 'left' }} />
  </motion.div>
);

const Btn = ({ children, onClick, variant = 'ghost', size = 'sm', disabled = false, style = {}, type = 'button' }) => {
  const V = {
    ghost:  { background: 'rgba(255,255,255,.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,.08)' },
    cyan:   { background: 'rgba(34,211,238,.15)',  color: '#22d3ee', border: '1px solid rgba(34,211,238,.3)'  },
    green:  { background: 'rgba(16,185,129,.15)',  color: '#34d399', border: '1px solid rgba(16,185,129,.3)'  },
    red:    { background: 'rgba(239,68,68,.15)',   color: '#f87171', border: '1px solid rgba(239,68,68,.3)'   },
    amber:  { background: 'rgba(245,158,11,.15)',  color: '#f59e0b', border: '1px solid rgba(245,158,11,.3)'  },
  };
  const v = V[variant] || V.ghost;
  const pad = size === 'xs' ? '3px 8px' : size === 'sm' ? '5px 12px' : '8px 18px';
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{ ...v, padding: pad, borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .45 : 1, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'opacity .15s', whiteSpace: 'nowrap', ...style }}>
      {children}
    </button>
  );
};

const Toggle = ({ value, onChange, accent = '#22d3ee' }) => (
  <div onClick={onChange}
    style={{ width: 36, height: 20, borderRadius: 10, background: value ? accent : 'rgba(255,255,255,.1)', position: 'relative', transition: 'background .2s', flexShrink: 0, cursor: 'pointer' }}>
    <motion.div animate={{ x: value ? 18 : 2 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      style={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.3)' }} />
  </div>
);

const FieldLabel = ({ children }) => (
  <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 5 }}>{children}</label>
);

const DI = ({ value, onChange, placeholder, type = 'text', style = {} }) => (
  <input type={type} value={value ?? ''} onChange={onChange} placeholder={placeholder}
    style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '8px 11px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none', ...style }} />
);

// ─── ConfirmDialog ────────────────────────────────────────────────────────
const ConfirmDialog = ({ open, onConfirm, onCancel }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 60 }}>
        <motion.div initial={{ scale: .92, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .92 }}
          style={{ background: '#0d1526', border: '1px solid rgba(255,255,255,.12)', borderRadius: 16, padding: 28, maxWidth: 360, width: '100%', textAlign: 'center' }}>
          <AlertTriangle size={28} color="#f87171" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 }}>Delete Banner?</p>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 22, lineHeight: 1.6 }}>This banner will be permanently removed from the slideshow.</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
            <Btn variant="red" onClick={onConfirm}><Trash2 size={12} /> Delete</Btn>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Banner Drawer ────────────────────────────────────────────────────────
const EMPTY_FORM = { imageUrl: '', active: true, order: 0 };

const BannerDrawer = ({ open, onClose, form, setForm, onSubmit, submitting, isEdit }) => {
  const set = key => e => setForm(p => ({ ...p, [key]: e.target.value }));

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(4px)', zIndex: 40 }}
            onClick={onClose} />
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 38 }}
            style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 460, background: '#0d1526', borderLeft: '1px solid rgba(255,255,255,.1)', zIndex: 50, display: 'flex', flexDirection: 'column' }}
          >
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.16em', color: 'rgba(34,211,238,.7)', marginBottom: 3 }}>{isEdit ? 'Editing' : 'New Banner'}</p>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{isEdit ? 'Edit Banner' : 'Add Banner'}</h3>
              </div>
              <button onClick={onClose} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}><X size={14} /></button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* Image URL */}
                <div>
                  <FieldLabel>Image URL *</FieldLabel>
                  <DI value={form.imageUrl} onChange={set('imageUrl')} placeholder="https://example.com/banner.jpg" />
                </div>

                {/* Image preview */}
                <AnimatePresence>
                  {form.imageUrl && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                      <FieldLabel>Preview</FieldLabel>
                      <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,.08)', position: 'relative', height: 140 }}>
                        <img
                          src={form.imageUrl} alt="Banner preview"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => { e.target.style.opacity = 0; }}
                        />
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity .2s' }}
                          onMouseEnter={e => e.currentTarget.style.opacity = 1}
                          onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                          <Eye size={20} color="#fff" />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Order */}
                <div>
                  <FieldLabel>Display Order</FieldLabel>
                  <DI type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} placeholder="0" />
                  <p style={{ fontSize: 10, color: '#334155', marginTop: 4 }}>Lower number = shown first in slideshow</p>
                </div>

                {/* Active toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(34,211,238,.06)', border: '1px solid rgba(34,211,238,.15)', borderRadius: 10, padding: '10px 14px', cursor: 'pointer' }}>
                  <Toggle value={form.active} onChange={() => setForm(p => ({ ...p, active: !p.active }))} accent="#22d3ee" />
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>Active</p>
                    <p style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>Show this banner in the slideshow</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,.06)', display: 'flex', gap: 10, flexShrink: 0 }}>
              <Btn variant="ghost" onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>Cancel</Btn>
              <Btn variant="cyan" onClick={onSubmit} disabled={submitting} style={{ flex: 2, justifyContent: 'center' }}>
                {submitting
                  ? <><div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(34,211,238,.4)', borderTopColor: '#22d3ee', animation: 'spin .7s linear infinite' }} />{isEdit ? 'Saving…' : 'Adding…'}</>
                  : isEdit ? <><Check size={13} /> Save Changes</> : <><Plus size={13} /> Add Banner</>
                }
              </Btn>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ─── Banner Card ──────────────────────────────────────────────────────────
const BannerCard = ({ banner, onEdit, onDelete, onToggleActive, index }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * .05, duration: .4, ease: [.22, 1, .36, 1] }}
      whileHover={{ y: -4, transition: { duration: .2 } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'rgba(17,24,39,.9)',
        border: `1px solid ${banner.active ? 'rgba(34,211,238,.2)' : 'rgba(255,255,255,.06)'}`,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: banner.active ? '0 0 20px rgba(34,211,238,.07)' : 'none',
      }}
    >
      {/* Top strip */}
      <div style={{ height: 3, background: banner.active ? 'linear-gradient(90deg,#22d3ee,#38bdf8)' : 'rgba(100,116,139,.3)' }} />

      {/* Image area */}
      <div style={{ position: 'relative', height: 140, background: 'rgba(255,255,255,.03)', overflow: 'hidden' }}>
        <img
          src={banner.imageUrl} alt={`Banner ${banner.order + 1}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .3s', transform: hovered ? 'scale(1.04)' : 'scale(1)' }}
          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
        />
        {/* Fallback */}
        <div style={{ display: 'none', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, background: 'rgba(255,255,255,.03)' }}>
          <ImageIcon size={28} color="#334155" />
          <p style={{ fontSize: 10, color: '#334155' }}>Image not found</p>
        </div>

        {/* Hover overlay */}
        <AnimatePresence>
          {hovered && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <button onClick={() => onEdit(banner)}
                style={{ background: 'rgba(34,211,238,.2)', border: '1px solid rgba(34,211,238,.4)', borderRadius: 10, padding: '8px 14px', color: '#22d3ee', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Edit2 size={12} /> Edit
              </button>
              <button onClick={() => onDelete(banner.id)}
                style={{ background: 'rgba(239,68,68,.2)', border: '1px solid rgba(239,68,68,.4)', borderRadius: 10, padding: '8px 14px', color: '#f87171', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Trash2 size={12} /> Delete
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Order badge */}
        <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 8, padding: '3px 8px', fontSize: 10, fontWeight: 700, color: '#e2e8f0' }}>
          #{banner.order + 1}
        </div>

        {/* Inactive overlay */}
        {!banner.active && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ background: 'rgba(100,116,139,.3)', border: '1px solid rgba(100,116,139,.4)', borderRadius: 8, padding: '4px 10px', fontSize: 10, fontWeight: 700, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 5 }}>
              <EyeOff size={11} /> Inactive
            </span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 10, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{banner.imageUrl}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{
            background: banner.active ? 'rgba(16,185,129,.12)' : 'rgba(100,116,139,.12)',
            color: banner.active ? '#34d399' : '#94a3b8',
            boxShadow: `inset 0 0 0 1px ${banner.active ? 'rgba(16,185,129,.3)' : 'rgba(100,116,139,.25)'}`,
            fontSize: 9, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
            padding: '2px 7px', borderRadius: 20,
          }}>
            {banner.active ? 'Active' : 'Inactive'}
          </span>
          <Toggle value={banner.active} onChange={() => onToggleActive(banner)} accent="#22d3ee" />
        </div>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function BannerManager() {
  const [banners, setBanners]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [submitting, setSubmitting]         = useState(false);
  const [slideshowEnabled, setSlideshowEnabled] = useState(true);
  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [editingId, setEditingId]           = useState(null);
  const [confirmId, setConfirmId]           = useState(null);
  const [filterActive, setFilterActive]     = useState('all');

  // ── fetch ──────────────────────────────────────────────────────────
  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'banners'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      setBanners(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      const settingsSnap = await getDocs(collection(db, 'settings'));
      const settings = settingsSnap.docs.find(d => d.id === 'bannerSettings');
      if (settings) setSlideshowEnabled(settings.data().slideshowEnabled ?? true);
    } catch { toast.error('Failed to load banners'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  // ── slideshow toggle ───────────────────────────────────────────────
  const toggleSlideshow = async () => {
    try {
      await setDoc(doc(db, 'settings', 'bannerSettings'), { slideshowEnabled: !slideshowEnabled }, { merge: true });
      setSlideshowEnabled(v => !v);
      toast.success(`Slideshow ${!slideshowEnabled ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to update slideshow setting'); }
  };

  // ── toggle active ──────────────────────────────────────────────────
  const handleToggleActive = async (banner) => {
    try {
      await setDoc(doc(db, 'banners', banner.id), { active: !banner.active }, { merge: true });
      setBanners(p => p.map(b => b.id === banner.id ? { ...b, active: !b.active } : b));
      toast.success(`Banner ${!banner.active ? 'activated' : 'deactivated'}`);
    } catch { toast.error('Failed to update banner'); }
  };

  // ── open drawer ────────────────────────────────────────────────────
  const openAdd  = () => { setForm({ ...EMPTY_FORM, order: banners.length }); setEditingId(null); setDrawerOpen(true); };
  const openEdit = b  => { setForm({ ...EMPTY_FORM, ...b }); setEditingId(b.id); setDrawerOpen(true); };

  // ── submit ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.imageUrl.trim()) { toast.error('Image URL is required'); return; }
    setSubmitting(true);
    try {
      if (editingId) {
        await setDoc(doc(db, 'banners', editingId), form, { merge: true });
        toast.success('Banner updated!');
      } else {
        await setDoc(doc(collection(db, 'banners')), { ...form, createdAt: new Date() });
        toast.success('Banner added!');
      }
      setDrawerOpen(false); setEditingId(null); fetchBanners();
    } catch { toast.error(editingId ? 'Failed to update' : 'Failed to add'); }
    finally { setSubmitting(false); }
  };

  // ── delete ─────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    try {
      await deleteDoc(doc(db, 'banners', confirmId));
      setBanners(p => p.filter(b => b.id !== confirmId));
      toast.success('Banner deleted!');
    } catch { toast.error('Failed to delete'); }
    finally { setConfirmId(null); }
  };

  // ── stats ──────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:    banners.length,
    active:   banners.filter(b => b.active).length,
    inactive: banners.filter(b => !b.active).length,
  }), [banners]);

  const filtered = useMemo(() => {
    if (filterActive === 'active')   return banners.filter(b => b.active);
    if (filterActive === 'inactive') return banners.filter(b => !b.active);
    return banners;
  }, [banners, filterActive]);

  // ─── LOADING ────────────────────────────────────────────────────────
  if (loading && banners.length === 0) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
    <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(255,0,0,.3)', borderTopColor: 'red', animation: 'spin .8s linear infinite' }} />      <p style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Loading banners…</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ─── RENDER ─────────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ paddingBottom: 48 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input::placeholder{color:#334155}`}</style>

      {/* ── Hero Banner ── */}
      <motion.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5, ease: [.22, 1, .36, 1] }}
        style={{ position: 'relative', overflow: 'hidden', borderRadius: 18, background: 'linear-gradient(120deg,#0f172a 0%,#1a1f35 50%,#0f172a 100%)', border: '1px solid rgba(255,255,255,.07)', padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle,rgba(34,211,238,.12),transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -30, left: '20%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle,rgba(56,189,248,.08),transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.2em', color: 'red', marginBottom: 5 }}>Media</p>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
              <ImageIcon size={20} color="red" strokeWidth={2} /> Banner Management
            </h1>
            <p style={{ fontSize: 11, color: '#475569' }}>Manage homepage banner slideshow</p>
          </div>

          {/* Slideshow global toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 10, padding: '8px 14px' }}>
              <Globe size={13} color={slideshowEnabled ? '#22d3ee' : '#475569'} />
              <span style={{ fontSize: 11, fontWeight: 600, color: slideshowEnabled ? '#e2e8f0' : '#475569' }}>Slideshow</span>
              <Toggle value={slideshowEnabled} onChange={toggleSlideshow} accent="#22d3ee" />
            </div>
            <Btn variant="ghost" onClick={fetchBanners} disabled={loading}>
              <RefreshCw size={13} style={{ animation: loading ? 'spin .8s linear infinite' : 'none' }} />
            </Btn>
            <Btn variant="cyan" onClick={openAdd}>
              <Plus size={13} /> Add Banner
            </Btn>
          </div>
        </div>
      </motion.div>

      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12, marginBottom: 20 }}>
        <MiniStat label="Total Banners" value={stats.total}    accent="#22d3ee" icon={ImageIcon} index={0} />
        <MiniStat label="Active"        value={stats.active}   accent="#34d399" icon={Eye}       index={1} />
        <MiniStat label="Inactive"      value={stats.inactive} accent="#94a3b8" icon={EyeOff}    index={2} />
        <MiniStat label="Slideshow"     value={slideshowEnabled ? 'ON' : 'OFF'} accent={slideshowEnabled ? '#22d3ee' : '#475569'} icon={Layers} index={3} />
      </div>

      {/* ── Filter tabs ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[['all', 'All'], ['active', 'Active'], ['inactive', 'Inactive']].map(([val, label]) => (
          <button key={val} onClick={() => setFilterActive(val)} style={{
            padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            background: filterActive === val ? 'rgba(34,211,238,.15)' : 'rgba(255,255,255,.05)',
            color: filterActive === val ? '#22d3ee' : '#475569',
            border: filterActive === val ? '1px solid rgba(34,211,238,.3)' : '1px solid rgba(255,255,255,.07)',
            transition: 'all .15s',
          }}>{label}</button>
        ))}
        <p style={{ fontSize: 10, color: '#334155', marginLeft: 'auto', alignSelf: 'center' }}>
          <span style={{ color: '#22d3ee', fontWeight: 700 }}>{filtered.length}</span> / {banners.length} banners
        </p>
      </div>

      {/* ── Banners Grid ── */}
      {filtered.length === 0 ? (
        <GlassPanel style={{ padding: '48px 24px', textAlign: 'center' }}>
          <ImageIcon size={32} style={{ margin: '0 auto 12px', opacity: .2, display: 'block', color: '#475569' }} />
          <p style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
            {filterActive !== 'all' ? `No ${filterActive} banners` : 'No banners yet'}
          </p>
          {filterActive === 'all' && (
            <div style={{ marginTop: 14 }}><Btn variant="cyan" onClick={openAdd}><Plus size={12} /> Add your first banner</Btn></div>
          )}
        </GlassPanel>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
          {filtered.map((banner, idx) => (
            <BannerCard
              key={banner.id}
              banner={banner}
              onEdit={openEdit}
              onDelete={id => setConfirmId(id)}
              onToggleActive={handleToggleActive}
              index={idx}
            />
          ))}
        </div>
      )}

      {/* ── Drawer + Confirm ── */}
      <BannerDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingId(null); }}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        submitting={submitting}
        isEdit={!!editingId}
      />

      <ConfirmDialog
        open={!!confirmId}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmId(null)}
      />
    </motion.div>
  );
}